import { useCallback, useRef, useState, lazy, Suspense } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { FormikProps } from "formik"
import { Integration } from "../../types"
import { useAppDispatch, useToast } from "../../common/hooks"
import BBDialog from "../../common/ui/dialog"
import { RequireRole } from "../../common/validation"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants"
import {
  useGetIntegrationDetailsQuery,
  useGetStatusQuery,
  useDeleteIntegrationMutation,
  useSaveIntegrationMutation
} from "../../redux/queries"
import { setPreviewModal } from "../../redux/slices/integrations"
import { useSaveIntegration } from "./hooks/useSaveIntegration"
import IntegrationHeader from "./components/IntegrationHeader"
import IntegrationTabContent from "./components/IntegrationTabContent"

const PreviewModal = lazy(() => import("../../common/ui/preview-modal"))

const IntegrationDetailsPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { pushSuccessToast, pushErrorToast } = useToast()

  const formIntegrationRef = useRef<FormikProps<Integration>>(null)
  const [tab, setTab] = useState("settings")
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)

  const { data: integration, isLoading: isLoadingIntegration } =
    useGetIntegrationDetailsQuery(id ?? "", { skip: !id })

  const { data: statusData = [], isLoading: isLoadingStatus } =
    useGetStatusQuery(
      { integrationID: integration?.id ?? "" },
      { skip: !integration?.id }
    )

  const [saveIntegration] = useSaveIntegrationMutation()
  const [deleteIntegration] = useDeleteIntegrationMutation()

  const onSaveSettings = useSaveIntegration({
    saveIntegration,
    pushSuccessToast,
    pushErrorToast
  })

  const handleDelete = async () => {
    if (!integration?.id) return
    const res = await deleteIntegration(integration.id)
    if ("data" in res) {
      pushSuccessToast("Integration has been deleted successfully")
      navigate("/integrations")
    } else {
      pushErrorToast("Failed to delete integration")
    }
    setDeleteModalVisible(false)
  }

  const onClickPreview = useCallback(() => {
    if (integration?.app_key) {
      dispatch(setPreviewModal({ isOpen: true, app_key: integration.app_key }))
    }
  }, [dispatch, integration?.app_key])

  const isLoading = isLoadingIntegration || !integration

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE.integrations}>
      <div className="min-h-full">
        <IntegrationHeader
          integration={integration}
          isLoading={isLoading}
          id={id ?? ""}
          tab={tab}
          onTabChange={setTab}
          onClickPreview={onClickPreview}
          onDeleteClick={() => setDeleteModalVisible(true)}
        />

        <IntegrationTabContent
          tab={tab}
          integration={integration}
          isLoading={isLoading}
          isLoadingStatus={isLoadingStatus}
          statusData={statusData}
          formIntegrationRef={formIntegrationRef}
          onSaveSettings={onSaveSettings}
          navigate={navigate}
        />

        <Suspense fallback={null}>
          <PreviewModal
            onClose={() =>
              dispatch(setPreviewModal({ isOpen: false, appKey: "" }))
            }
          />
        </Suspense>

        <BBDialog
          open={deleteModalVisible}
          onClose={() => setDeleteModalVisible(false)}
          title="Delete integration"
          body="Are you sure you want to delete this integration?"
          okLabel="Yes, Delete"
          cancelLabel="Cancel"
          onOk={handleDelete}
          onCancel={() => setDeleteModalVisible(false)}
        />
      </div>
    </RequireRole>
  )
}

export default IntegrationDetailsPage
